import {
  AccountingTypeLabelModifier,
  accountingType,
} from '@/features/master-table/models';
import {
  type ReportOrganizingTableFormat1Model,
  type ReportOrganizingTableFormat1SummaryModel,
  format1ColumnType,
} from '@/features/mold/models/organizing-table';
import type {
  projectDetailUrlTypeEnum,
  reportFileType,
} from '@/features/mold/models/report';
import { getProjectDetailPageUrl } from '@/features/project/_utils/project';
import {
  ActionOfReflectionStatusLabelModifier,
  InspectionByExternalExpertReasonModifier,
  OpinionByAdministrativeReviewTeamLabelModifier,
} from '@/features/project/projects/_models/project';
import { t } from '@/i18n';
import {
  SPACE_TEXT,
  convertToNegative,
  createCsv,
  formatAmountForCsv,
  formatAmountReport,
  validateTotalWithElements,
} from '@/utils';
import { ReviewTypeLabelModifier, reviewTypeEnum } from '@/utils/enum';
import type { z } from 'zod';

export type SummaryTotalType = 'total' | 'rsTotal' | 'skipRsTotal';
type SummaryType = z.infer<typeof ReportOrganizingTableFormat1SummaryModel>;

type FileType = z.infer<typeof reportFileType>;

type MoneyConvertParams = {
  value: number | null;
  type: FileType;
};

type SummaryConvertParams = {
  values: SummaryType;
  fileType: FileType;
  totalType: SummaryTotalType;
};

export const format1HeaderModifier = ({
  currentYear,
  isCsv,
}: {
  currentYear: number;
  isCsv?: boolean;
}) => ({
  policyLevel1: t('policy-level-2'),
  policyLevel2: t('policy-level-3'),
  projectNumber: isCsv
    ? t('project-number')
    : t('report.format1.project-number'),
  name: t('project-name'),
  startYear: t('start-fiscal-year-of-project'),
  endYear: t('end-fiscal-year-of-project'),
  priorFinalizedAmountTotal: t('prior-finalized-amount-total', {
    year: currentYear - 1,
  }),
  priorUsableFinalizedAmountTotal: t('fiscal-year-with-value', {
    year: currentYear - 1,
  }),
  externalOpinion: t('findings-of-external-experts'),
  adminReformReview: t('admin-reform-review-context'),
  initialBudgetTotal: t('initial-budget-total', { year: currentYear }),
  posteriorRequestedAmountTotal: t('posterior-requested-amount-total', {
    year: currentYear + 1,
  }),
  differencePosteriorRequestedSelfInitialBudget: t(
    'difference-posterior-requested-self-initial-budget',
  ),
  reflectionStatus: t('reflection-status'),
  parentOrganization: t('organization-in-charge'),
  accountTitle: t('accounting-type'),
  priorProjectNumbers: t('prior-project-numbers', { year: currentYear - 1 }),
  externalEvaluationScope: t('external-evaluation-scope', {
    year: currentYear,
  }),
  externalEvaluationReason: t('external-evaluation-reason', {
    year: currentYear,
  }),
  externalEvaluationLastExecutionYear: t(
    'external-evaluation-last-execution-year',
  ),
  projectDetailPageUrl: t('project-detail-page-url'),
  accountingType: '',
  priorExecutionAmountTotal: '',
  executableAmount: isCsv
    ? t('budgetary-sum-csv', { year: currentYear - 1 })
    : t('budgetary-sum'),
  executionAmount: isCsv
    ? t('execution-amount-csv', { year: currentYear - 1 })
    : t('execution-amount'),
  reflectedAmount: t('reflected-amount'),
  reflectedContents: t('improvement-and-reflection-status'),
});

export const format1ValueConverter = {
  accountingType: (
    type: SummaryType['accountingType'],
    accountTitle: SummaryType['accountTitle'],
  ) =>
    type === 'special'
      ? (accountTitle ?? SPACE_TEXT)
      : AccountingTypeLabelModifier[accountingType.Values.general],
  externalEvaluationScope: (value: string | null): string =>
    value ? ReviewTypeLabelModifier[value] : '',
  reflectedAmount: (params: MoneyConvertParams) =>
    formatAmountReport(params.value, params.type),
  previousFinalizedAmountTotal: (
    params: MoneyConvertParams & {
      negativeCount: number | null;
      isDebugMode: boolean;
      rawValue?: number | null;
    },
  ) =>
    formatAmountReport(params.value, params.type, {
      negativeCount: params.negativeCount,
      isDebugMode: params.isDebugMode,
      rawValue: params.rawValue,
    }),
  executableAmount: (
    params: MoneyConvertParams & {
      negativeCount: number | null;
      isDebugMode: boolean;
      rawValue?: number | null;
    },
  ) =>
    formatAmountReport(params.value, params.type, {
      negativeCount: params.negativeCount,
      isDebugMode: params.isDebugMode,
      rawValue: params.rawValue,
    }),
  executionAmount: (
    params: MoneyConvertParams & {
      negativeCount: number | null;
      isDebugMode: boolean;
      rawValue?: number | null;
    },
  ) =>
    formatAmountReport(params.value, params.type, {
      negativeCount: params.negativeCount,
      isDebugMode: params.isDebugMode,
      rawValue: params.rawValue,
    }),
  differencePosteriorRequestedSelfInitialBudget: (
    params: MoneyConvertParams & {
      initialBudgetTotal: number | null;
      rawInitialBudgetTotal: number | null;
      negativeInitialBudgetCount: number | null;
      posteriorRequestedAmountTotal: number | null;
      rawPosteriorRequestedAmountTotal: number | null;
      negativePosteriorRequestedAmountCount: number | null;
      isDebugMode: boolean;
    },
  ) => {
    const { result } = validateTotalWithElements({
      totalValue: params.value,
      elements: [
        {
          value: params.initialBudgetTotal,
          rawValue: params.rawInitialBudgetTotal,
          negativeCount: params.negativeInitialBudgetCount,
        },
        {
          value: convertToNegative(params.posteriorRequestedAmountTotal),
          rawValue: convertToNegative(params.rawPosteriorRequestedAmountTotal),
          negativeCount: params.negativePosteriorRequestedAmountCount,
        },
      ],
    });

    return formatAmountReport(params.value, params.type, {
      isDebugMode: params.isDebugMode,
      negativeCount: result === 'isNull' ? 1 : 0,
    });
  },
  summaryInitialBudgetTotal: ({
    totalType,
    fileType,
    values,
  }: SummaryConvertParams) => {
    return formatAmountReport(
      totalType === 'total'
        ? values.comprehensivePreviousInitialBudgetTotal
        : totalType === 'rsTotal'
          ? values.administrativeReformTeamEvaluationInitialBudgetTotal
          : values.skipAdministrativeReformTeamEvaluationInitialBudgetTotal,
      fileType,
    );
  },
  summaryPosteriorRequestedAmountTotal: ({
    totalType,
    fileType,
    values,
  }: SummaryConvertParams) => {
    return formatAmountReport(
      totalType === 'total'
        ? values.comprehensivePosteriorRequestedAmountTotal
        : totalType === 'rsTotal'
          ? values.administrativeReformTeamEvaluationPosteriorRequestedAmountTotal
          : values.skipAdministrativeReformTeamEvaluationPosteriorRequestedAmountTotal,
      fileType,
    );
  },
  summaryDifferencePosteriorRequestedSelfInitialBudget: ({
    totalType,
    fileType,
    values,
  }: SummaryConvertParams) => {
    return formatAmountReport(
      totalType === 'total'
        ? values.comprehensiveDifferencePosteriorRequestedSelfInitialBudget
        : totalType === 'rsTotal'
          ? values.administrativeReformTeamEvaluationDifferencePosteriorRequestedSelfInitialBudget
          : values.skipAdministrativeReformTeamEvaluationDifferencePosteriorRequestedSelfInitialBudget,
      fileType,
    );
  },
  summaryPriorFinalizedAmountTotal: ({
    totalType,
    fileType,
    values,
  }: SummaryConvertParams) => {
    return formatAmountReport(
      totalType === 'total'
        ? values.comprehensivePreviousFinalizedAmountTotal
        : totalType === 'rsTotal'
          ? values.administrativeReformTeamEvaluationPreviousFinalizedAmountTotal
          : values.skipAdministrativeReformTeamEvaluationPreviousFinalizedAmountTotal,
      fileType,
    );
  },
  summaryPriorExecutionAmountTotal: ({
    totalType,
    fileType,
    values,
  }: SummaryConvertParams) => {
    return formatAmountReport(
      totalType === 'total'
        ? values.comprehensivePreviousExecutionAmountTotal
        : totalType === 'rsTotal'
          ? values.administrativeReformTeamEvaluationPreviousExecutionAmountTotal
          : values.skipAdministrativeReformTeamEvaluationPreviousExecutionAmountTotal,
      fileType,
    );
  },
  summaryReflectedAmount: ({
    totalType,
    fileType,
    values,
  }: SummaryConvertParams) => {
    return formatAmountReport(
      totalType === 'total'
        ? values.comprehensivePreviousReflectionStatusReflectedAmount
        : totalType === 'rsTotal'
          ? values.administrativeReformTeamEvaluationPreviousReflectionStatusReflectedAmount
          : values.skipAdministrativeReformTeamEvaluationPreviousReflectionStatusReflectedAmount,
      fileType,
    );
  },
  externalEvaluationReason: (value: string | null): string => {
    return value ? InspectionByExternalExpertReasonModifier[value] : '';
  },
  externalOpinion: (
    value: string | null,
    externalEvaluationScope: z.infer<typeof reviewTypeEnum> | null,
  ): string => {
    if (externalEvaluationScope === 'unnecessary') {
      return ReviewTypeLabelModifier[reviewTypeEnum.Values.unnecessary];
    }
    return value ?? '';
  },
  adminReformReviewOption: (value: string | null): string => {
    return value ? OpinionByAdministrativeReviewTeamLabelModifier[value] : '';
  },
  reflectionContentAction: (value: string | null): string => {
    return value ? ActionOfReflectionStatusLabelModifier[value] : '';
  },
};

export const getFormat1CsvData = ({
  year,
  reports,
  isDebugMode,
  projectDetailUrlType,
}: {
  year: number;
  reports: z.infer<typeof ReportOrganizingTableFormat1Model>;
  isDebugMode: boolean;
  projectDetailUrlType?: z.infer<typeof projectDetailUrlTypeEnum> | null;
}) => {
  const formattedReports = getFormattedReports(
    reports,
    isDebugMode,
    projectDetailUrlType,
  );

  return createCsv({
    headerKeys: format1ColumnType.options,
    headerConverter: format1HeaderModifier({ currentYear: year, isCsv: true }),
    body: formattedReports,
  });
};

const getFormattedReports = (
  reports: z.infer<typeof ReportOrganizingTableFormat1Model>,
  isDebugMode: boolean,
  projectDetailUrlType?: z.infer<typeof projectDetailUrlTypeEnum> | null,
) => {
  return reports.policies.flatMap<Record<string, string | number>>(
    (policyLevel1) => {
      return policyLevel1.policyMeasuresData.flatMap((policyLevel2) => {
        return policyLevel2.projectsData.flatMap((projectData) => ({
          policyLevel1: policyLevel1.policyName ?? t('not-set'),
          policyLevel2: policyLevel2.measureName ?? t('not-set'),
          projectNumber: projectData.projectNumber,
          name: projectData.name,
          parentOrganization: projectData.parentOrganization,
          startYear: projectData.startYear ?? t('unknown-start'),
          endYear: projectData.endYear ?? t('no-scheduled-end'),
          accountTitle: format1ValueConverter.accountingType(
            projectData.accountingType,
            projectData.accountTitle,
          ),
          executableAmount: format1ValueConverter.executableAmount({
            value: projectData.previousFinalizedAmountTotal,
            negativeCount: projectData.negativePreviousFinalizedAmountCount,
            type: 'csv',
            isDebugMode,
          }),
          executionAmount: format1ValueConverter.executionAmount({
            value: projectData.previousExecutionAmountTotal,
            negativeCount: projectData.negativePreviousYearExecutionAmountCount,
            type: 'csv',
            isDebugMode,
          }),
          initialBudgetTotal: formatAmountForCsv(
            projectData.initialBudgetTotal,
            {
              negativeCount: projectData.negativeInitialBudgetCount,
              isDebugMode,
            },
          ),
          posteriorRequestedAmountTotal: formatAmountForCsv(
            projectData.posteriorRequestedAmountTotal,
            {
              negativeCount: projectData.negativePosteriorRequestedAmountCount,
              isDebugMode,
            },
          ),
          differencePosteriorRequestedSelfInitialBudget:
            format1ValueConverter.differencePosteriorRequestedSelfInitialBudget(
              {
                value:
                  projectData.differencePosteriorRequestedSelfInitialBudget,
                type: 'csv',
                initialBudgetTotal: projectData.initialBudgetTotal,
                rawInitialBudgetTotal: projectData.rawInitialBudgetTotal,
                negativeInitialBudgetCount:
                  projectData.negativeInitialBudgetCount,
                posteriorRequestedAmountTotal:
                  projectData.posteriorRequestedAmountTotal,
                rawPosteriorRequestedAmountTotal:
                  projectData.rawPosteriorRequestedAmountTotal,
                negativePosteriorRequestedAmountCount:
                  projectData.negativePosteriorRequestedAmountCount,
                isDebugMode,
              },
            ) ?? '',
          externalOpinion: format1ValueConverter.externalOpinion(
            projectData.externalOpinion,
            projectData.externalEvaluationScope,
          ),
          adminReformReview: format1ValueConverter.adminReformReviewOption(
            projectData.adminReformReview.opinion,
          ),
          reflectedAmount: format1ValueConverter.reflectedAmount({
            value: projectData.reflectionStatus.reflectedAmount,
            type: 'csv',
          }),
          reflectedContents: format1ValueConverter.reflectionContentAction(
            projectData.reflectionStatus.reflectionContent.action,
          ),
          externalEvaluationScope:
            format1ValueConverter.externalEvaluationScope(
              projectData.externalEvaluationScope,
            ),
          externalEvaluationReason:
            format1ValueConverter.externalEvaluationReason(
              projectData.externalEvaluationReason,
            ),
          externalEvaluationLastExecutionYear:
            projectData.externalEvaluationLastExecutionYear ?? '',
          projectDetailPageUrl: getProjectDetailPageUrl({
            isPS: projectDetailUrlType === 'public',
            projectId: projectData.id,
          }),
        }));
      });
    },
  );
};
